import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { playSound, SOUNDS } from '../App';

// Tooltip descriptions for each icon
const ICON_TOOLTIPS = {
  mycomputer: 'Se systeminfo og konfigurasjoner',
  network: 'Nettverksinnstillinger og tilkoblinger',
  welcome: 'Start her - lær om Autonomous',
  recycle: 'Slettede filer',
  internet: 'Surf på nettet',
  winamp: 'Spill musikk med Winamp',
  appwizard: 'Bygg din egen app - interaktiv demo',
  services: 'Se hva vi kan bygge for deg',
  metrics: 'Nøkkeltall og statistikk',
  about: 'Om Autonomous og vårt team',
  contact: 'Ta kontakt med oss',
  readme: 'Viktig informasjon',
  video: 'Se demo av våre prosjekter',
  minesweeper: 'Klassisk Minesveiper-spill',
  cmd: 'MS-DOS kommandolinje'
};

export default function DesktopIcon({ 
  id, 
  label, 
  icon: Icon,
  IconComponent,
  x, 
  y, 
  selected,
  highlight,
  glowing,
  onDoubleClick, 
  onClick,
  onContextMenu 
}) {
  const [isPressed, setIsPressed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Show tooltip after hover delay
  React.useEffect(() => {
    let timer;
    if (isHovered && !selected) {
      timer = setTimeout(() => setShowTooltip(true), 800);
    } else {
      setShowTooltip(false);
    }
    return () => clearTimeout(timer);
  }, [isHovered, selected]);

  const handleClick = (e) => {
    e.stopPropagation();
    playSound(SOUNDS.click);
    onClick?.();
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    playSound(SOUNDS.windowOpen);
    onDoubleClick?.();
  };

  const handleContextMenu = (e) => {
    playSound(SOUNDS.click);
    onContextMenu?.(e);
  };

  const handleMouseDown = () => setIsPressed(true);
  const handleMouseUp = () => setIsPressed(false);
  const handleMouseLeave = () => {
    setIsPressed(false);
    setIsHovered(false);
  };
  const handleMouseEnter = () => setIsHovered(true);

  // Special icons that get highlighted
  const isSpecial = highlight && (id === 'welcome' || id === 'appwizard');
  
  return (
    <motion.div
      className={`desktop-icon ${selected ? 'selected' : ''} ${highlight ? 'highlight' : ''} ${glowing ? 'glowing' : ''}`}
      style={{ left: x, top: y }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      initial={{ opacity: 0, y: 15, scale: 0.8 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        scale: glowing ? 1.08 : (isPressed ? 0.95 : (isHovered ? 1.03 : 1))
      }}
      transition={{ 
        duration: 0.25,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      whileHover={{ y: -2 }}
      data-testid={`desktop-icon-${id}`}
    >
      {/* Glowing effect when highlighted from WelcomeGuide */}
      <AnimatePresence>
        {glowing && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ 
              opacity: [0.5, 0.8, 0.5],
              scale: [1, 1.3, 1]
            }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ 
              duration: 1.2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -55%)',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(0, 128, 255, 0.6) 0%, rgba(0, 128, 255, 0.2) 50%, transparent 70%)',
              pointerEvents: 'none',
              zIndex: -1
            }}
          />
        )}
      </AnimatePresence>

      {/* Subtle glow for special icons */}
      {isSpecial && !glowing && (
        <motion.div
          animate={{ 
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -55%)',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            background: id === 'appwizard' 
              ? 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(255, 215, 0, 0.4) 0%, transparent 70%)',
            pointerEvents: 'none',
            zIndex: -1
          }}
        />
      )}

      {/* Hover ring effect */}
      <AnimatePresence>
        {isHovered && !selected && !glowing && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.3, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -55%)',
              width: '56px',
              height: '56px',
              borderRadius: '8px',
              border: '2px solid rgba(0, 0, 128, 0.3)',
              background: 'rgba(0, 0, 128, 0.05)',
              pointerEvents: 'none',
              zIndex: -1
            }}
          />
        )}
      </AnimatePresence>
      
      <div className={`desktop-icon-image ${highlight ? 'highlight-icon' : ''} ${glowing ? 'glowing-icon' : ''}`}>
        {IconComponent ? <IconComponent /> : (Icon && <Icon size={32} />)}
      </div>
      <span className={`desktop-icon-label ${highlight ? 'highlight-label' : ''}`}>
        {label}
      </span>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && ICON_TOOLTIPS[id] && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: '8px',
              padding: '6px 10px',
              background: '#ffffcc',
              border: '1px solid #000',
              boxShadow: '2px 2px 0 rgba(0,0,0,0.3)',
              fontFamily: "'Segoe UI', Tahoma, sans-serif",
              fontSize: '11px',
              color: '#000',
              whiteSpace: 'nowrap',
              zIndex: 1000,
              pointerEvents: 'none'
            }}
          >
            {ICON_TOOLTIPS[id]}
            {/* Tooltip arrow */}
            <div style={{
              position: 'absolute',
              top: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid #000'
            }} />
            <div style={{
              position: 'absolute',
              top: '-5px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderBottom: '5px solid #ffffcc'
            }} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
